import { MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type {
	MillSize,
	RollApplication,
	RollBlank,
	RollBlankAttachment,
	RollMaterial,
} from "../models/RollBlank";

export const getRollBlanks = async ({
	id = null,
}: {
	id?: number | null;
} = {}): Promise<RollBlank[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "OnlyActives", value: false }];

	if (id) {
		parameters.push({ name: "Id", value: id });
	}

	const resp = await apiService.callV2("[ROLLS].[GetRollBlanks]", parameters);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as RollBlank[];
	}
	throw new Error(resp.message || "Error fetching roll blanks");
};

export const upsertRollBlank = async (blank: RollBlank): Promise<RollBlank> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "Id", value: blank.Id },
		{ name: "Dwg", value: blank.Dwg || "-" },
		{ name: "MillSizeId", value: blank.MillSizeId ?? -1 },
		{ name: "Description", value: blank.Description || "-" },
		{ name: "Width", value: blank.Width ?? -1 },
		{ name: "NewDiameter", value: blank.NewDiameter ?? -1 },
		{ name: "ScrapDiameter", value: blank.ScrapDiameter ?? -1 },
		{ name: "EstRCost", value: blank.EstRCost ?? -1 },
		{ name: "EstSCost", value: blank.EstSCost ?? -1 },
		{ name: "DTol", value: blank.DTol ?? -1 },
		{ name: "DTolm", value: blank.DTolm ?? -1 },
		{ name: "RollApplicationId", value: blank.RollApplicationId ?? -1 },
		{ name: "RollMaterialId", value: blank.RollMaterialId ?? -1 },
		{ name: "HardnessMinimum", value: blank.HardnessMinimum ?? -1 },
		{ name: "HardnessMaximum", value: blank.HardnessMaximum ?? -1 },
		{ name: "PitProgramName", value: blank.PitProgramName || "-" },
		{ name: "Active", value: blank.Active },
	];

	const resp = await apiService.callV2("[ROLLS].[UpsertRollBlank]", parameters);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows[0]", null) as RollBlank;
	}
	throw new Error(resp.message || "Error saving roll blank");
};

export const removeRollBlank = async (id: number): Promise<void> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "Id", value: id }];

	const resp = await apiService.callV2("[ROLLS].[RemoveRollBlank]", parameters);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting roll blank");
	}
};

export const getMillSizes = async (): Promise<MillSize[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "OnlyActives", value: true }];

	const resp = await apiService.callV2("[MILL].[GetMillSizes]", parameters);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as MillSize[];
	}
	throw new Error(resp.message || "Error fetching mill sizes");
};

export const getRollApplications = async (): Promise<RollApplication[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "OnlyActives", value: true }];

	const resp = await apiService.callV2(
		"[ROLLS].[GetRollApplications]",
		parameters,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as RollApplication[];
	}
	throw new Error(resp.message || "Error fetching roll applications");
};

export const getRollMaterials = async (): Promise<RollMaterial[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "OnlyActives", value: true }];

	const resp = await apiService.callV2(
		"[ROLLS].[GetRollMaterials]",
		parameters,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as RollMaterial[];
	}
	throw new Error(resp.message || "Error fetching roll materials");
};

// ============================================
// ROLL BLANK ATTACHMENTS
// ============================================

export const getRollBlankAttachments = async (
	rollBlankId: number,
): Promise<RollBlankAttachment[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "RollBlankId", value: rollBlankId },
	];

	const resp = await apiService.callV2(
		"[ROLLS].[GetRollBlankAttachments]",
		parameters,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as RollBlankAttachment[];
	}
	throw new Error(resp.message || "Error fetching roll blank attachments");
};

export interface UpsertRollBlankAttachmentParams {
	rollBlankId: number;
	uploadFileId: number;
	attachmentName: string;
	attachmentURL: string;
}

export const upsertRollBlankAttachment = async (
	params: UpsertRollBlankAttachmentParams,
): Promise<RollBlankAttachment> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "RollBlankId", value: params.rollBlankId },
		{ name: "UploadFileId", value: params.uploadFileId },
		{ name: "AttachmentName", value: params.attachmentName },
		{ name: "AttachmentURL", value: params.attachmentURL },
	];

	const resp = await apiService.callV2(
		"[ROLLS].[UpsertRollBlankAttachment]",
		parameters,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows[0]", null) as RollBlankAttachment;
	}
	throw new Error(resp.message || "Error saving roll blank attachment");
};

export const deleteRollBlankAttachment = async (
	rollBlankAttachmentId: number,
): Promise<void> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "RollBlankAttachmentId", value: rollBlankAttachmentId },
	];

	const resp = await apiService.callV2(
		"[ROLLS].[DeleteRollBlankAttachment]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting roll blank attachment");
	}
};
